import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  FunctionComponent,
} from 'react';
import AuthService from '../services/AuthService';
import Loader from 'components/Common/Loader';
import NIService, { INIService } from 'services/NIService';
import {
  updateSidebarItems,
  setMapPopupState,updateDirtyState
} from 'features/applicationState';
import { useDispatch } from 'react-redux';
import UserManager from 'utils/UserManager';
import { useNavigate } from 'react-router-dom';

interface NiKendoContextProps {
  niKendoModule: any;
  isLoading: boolean;
}

export const NiKendoContext = createContext<NiKendoContextProps>({
  niKendoModule: null,
  isLoading: true,
});

export const NiKendoProvider: FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [niKendoModule, setNiKendoModule] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);  // Store navigate in a ref
  navigateRef.current = navigate;  // Always keep the current `navigate` function

  const initNiKendoApp = (niKendoApp: any, token: any, apps: any[]) => {
    if (niKendoApp && typeof niKendoApp.init === 'function') {
      return niKendoApp
        .init(token, apps)
        .then(() => {
          setNiKendoModule(niKendoApp);
          setIsLoading(false);
        })
        .catch((error: any) => {
          console.error('Error during niKendoApp.init:', error);
          // Optionally handle error state or display an error message to the user
        });
    } else {
      console.error('niKendoApp.init is not a function');
      // Optionally handle error state or display an error message to the user
    }
  };

  useEffect(() => {
    const initNiKendoModule = async () => {
      await AuthService.initConfig();
      const user = AuthService.getUser();
      const niService: INIService = new NIService();
      const response = await niService.GetMenuItems();

      const refreshMenuItems = async (permission) => {
        const response = await niService.GetMenuItems();
        UserManager.addPermission(permission);
        dispatch(updateSidebarItems(response));
      };
      // store menu items in redux
      dispatch(updateSidebarItems(response));
      // eslint-disable-next-line no-undef
      if (process.env.NODE_ENV === 'development') {
        import('remoteMainEntry/mainApp')
          .then((module) => {
            const niKendoApp = module.default;
            initNiKendoApp(niKendoApp, user, response);
            niKendoApp.application.openMapPopup = (
              getGeoJsonCallback,
              geoJson,
              region
            ) => {
              var geoJsonObj = geoJson
                ? {
                    geometry: JSON.parse(geoJson),
                    properties: {},
                    type: 'Feature',
                  }
                : null;
              dispatch(
                setMapPopupState({
                  isVisible: true,
                  getGeoJsonCallback: getGeoJsonCallback,
                  geoJson: geoJsonObj,
                  region: region,
                })
              );
            };

            niKendoApp.application.openMapDashboard = (params) => {
              let paramsString = new URLSearchParams(params).toString();
              navigateRef.current(`/dashboard/ni-ran-advanced-map-dashboard?${paramsString}`);
            };

            niKendoApp.application.updateDirtyState = (isDirty) => { 
              dispatch(updateDirtyState(isDirty));
            };

            niKendoApp.application.refreshMenuItems = refreshMenuItems;
          })
          .catch((error) => {
            console.error('Failed to import module:', error);
            // Optionally handle error state or display an error message to the user
          });
      } else {
        import('@yuvo/ni-kendo-library') // Import the library
          .then((module) => {
            const niKendoApp = module.default;
            initNiKendoApp(niKendoApp, user, response);
            niKendoApp.application.openMapPopup = (
              getGeoJsonCallback,
              geoJson,
              region
            ) => {
              var geoJsonObj = geoJson
                ? {
                    geometry: JSON.parse(geoJson),
                    properties: {},
                    type: 'Feature',
                  }
                : null;
              dispatch(
                setMapPopupState({
                  isVisible: true,
                  getGeoJsonCallback: getGeoJsonCallback,
                  geoJson: geoJsonObj,
                  region: region,
                })
              );
            };

            niKendoApp.application.openMapDashboard = (params) => {
              let paramsString = new URLSearchParams(params).toString();
              navigateRef.current(`/dashboard/ni-ran-advanced-map-dashboard?${paramsString}`);
            };
 
            niKendoApp.application.updateDirtyState = (isDirty) => { 
              dispatch(updateDirtyState(isDirty));
            };
            
            niKendoApp.application.refreshMenuItems = refreshMenuItems;
          })
          .catch((error) => {
            console.error('Failed to import module:', error);
            // Optionally handle error state or display an error message to the user
          });
      }
    };

    initNiKendoModule();
  }, [dispatch]);

  // Memoize only niKendoModule
  const memoizedNiKendoModule = useMemo(() => niKendoModule, [niKendoModule]);

  const contextValue: NiKendoContextProps = {
    niKendoModule: memoizedNiKendoModule,
    isLoading,
  };

  // Add a loading indicator or initial state if needed during initialization
  if (isLoading) {
    return <Loader />;
  }

  return (
    <NiKendoContext.Provider value={contextValue}>
      {children}
    </NiKendoContext.Provider>
  );
};
